// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { DeliverableDTO } from './deliverable';
import { CollectionDTO } from './collection';
import { OrderDTO } from './order';
import { AssetDTO } from './asset';
import { StaffUserDTO } from './staff-user';
import { JobRequestStatus, FolderType } from '@shared/constants/job-request';
import { AssetType } from '@shared/constants/asset';
import container from '@core/di';
import { DeliverablesStore } from '@shared/stores';
import { computed, makeObservable, observable } from 'mobx';
import { Option } from '@shared/components/Select';

// Struct to store info for a jobRequest received from portal-api 
export interface JobRequestDTO {
  id: number;
  name: string;
  lastComment: string;
  dueDate: string;
  orderId: OrderDTO['id'];
  assetId: AssetDTO['id'];
  assetVisitInfoId: number;
  deliverables: Array<DeliverableDTO>;
  readyDeliverableId: number; // The task ready for the next role
  collections: Array<CollectionDTO>;
  vendor: string;
  responsibleVendorId: number;
  postProcessingTechnicianId: StaffUserDTO['id'];
  productionTechnicianId: StaffUserDTO['id'];
  observerId: StaffUserDTO['id'];
  pilotId: StaffUserDTO['id'];
  pilotName: string;
  status: JobRequestStatus;
  oldStatus: string;
  availableStatuses: Array<number>;
  possibleFlags: Array<Option>;
  folderType: FolderType;
  priority: number;
  tag1: string;
  tag2: string;
  lastModificationDate: string;
  rank: number;
  dimensionAge: Array<number>;
  dimensionPercentComplete: Array<number>;
  dimensionRank: Array<number>;
  dimensionExceptionCode: Array<number>;
  isBlocked?: boolean;
  serverName?: string;
  shareName?: string;
  folderName?: string;
  driveLetter?: string;
  misc?: unknown;
  emi: number;
  multipleTowers: number;
  highAltitude: number;
  underConstruction: number;
  roadAccessIssues: number;
  otherConditions: string;
  age: number;  // How many times this data has been read
  serverNameLiDAR?: string;
  folderNameLiDAR?: string;
  serverNameVisuals?: string;
  folderNameVisuals?: string;

  collectionShipped?: string;
  collectionTracking?: string;
  collectionReceived?: string;
  groundShipped?: string;
  groundTracking?: string;
  groundReceived?: string;
  liDARShipped?: string;
  liDARTracking?: string;
  liDARReceived?: string;

  hasACS?: boolean;
  hasOrbitQC?: boolean;
  dimensionReadyStatus: Array<number>;

  carMake?: string;
  carModel?: string;
  carRegistration?: string;
  teamName?: string;
}

export default class JobRequest {
  private deliverablesStore = container.get<DeliverablesStore>(DeliverablesStore.diToken);
  @observable dimensionName2Id: Map<string, number>;  // Lookup dimension ID from dimension name.

  id: number;
  name: string;
  lastComment: string;
  dueDate: string;
  orderId: OrderDTO['id'];
  assetId: AssetDTO['id'];
  assetVisitInfoId: number;
  assetType: AssetType;
  deliverables: Array<DeliverableDTO>;
  readyDeliverableId: number;
  collections: Array<CollectionDTO>;
  vendor: string;
  responsibleVendorId: number;
  postProcessingTechnicianId: StaffUserDTO['id'];
  productionTechnicianId: StaffUserDTO['id'];
  observerId: StaffUserDTO['id'];
  pilotId: StaffUserDTO['id'];
  pilotName: string;
  status: JobRequestStatus;
  oldStatus: string;
  availableStatuses: Array<number>;
  possibleFlags: Array<Option>;
  isBlocked: boolean;
  folderType: FolderType;
  priority: number;
  tag1: string;
  tag2: string;
  lastModificationDate: string;
  rank: number;
  dimensionAge: Array<number>;
  dimensionPercentComplete: Array<number>;
  dimensionRank: Array<number>;
  dimensionExceptionCode: Array<number>;
  dimensionReadyStatus: Array<number>;
  serverName: string;
  shareName: string;
  folderName: string;
  driveLetter: string;
  misc?: unknown; // var to pass optional info (kludge).
  emi: number;
  multipleTowers: number;
  highAltitude: number;
  underConstruction: number;
  roadAccessIssues: number;
  otherConditions: string;
  age: number;

  serverNameLiDAR?: string;
  folderNameLiDAR?: string;
  serverNameVisuals?: string;
  folderNameVisuals?: string;

  collectionShipped?: string;
  collectionTracking?: string;
  collectionReceived?: string;
  groundShipped?: string;
  groundTracking?: string;
  groundReceived?: string;
  liDARShipped?: string;
  liDARTracking?: string;
  liDARReceived?: string;

  constructor(dto: JobRequestDTO) {
    this.update(dto);

    // Get dict to lookup dimension ID from dimension name.	
    this.deliverablesStore.getDimensionName2Id().then(d => this.dimensionName2Id = d);  
    makeObservable(this);
  }

  get asJson(): JobRequestDTO {
    return {
      id: this.id,
      name: this.name,
      lastComment: this.lastComment,
      dueDate: this.dueDate,
      orderId: this.orderId,
      assetId: this.assetId,
      assetVisitInfoId: this.assetVisitInfoId,
      deliverables: this.deliverables,
      readyDeliverableId: this.readyDeliverableId,
      collections: this.collections,
      vendor: this.vendor,
      responsibleVendorId: this.responsibleVendorId,
      postProcessingTechnicianId: this.postProcessingTechnicianId,
      productionTechnicianId: this.productionTechnicianId,
      pilotId: this.pilotId,
      pilotName: this.pilotName,
      observerId: this.observerId,
      status: this.status,
      oldStatus: this.oldStatus,
      availableStatuses: this.availableStatuses,
      possibleFlags: this.possibleFlags,
      isBlocked: this.isBlocked,
      serverName: this.serverName,
      shareName: this.shareName,
      folderName: this.folderName,
      folderType: this.folderType,
      priority: this.priority,
      tag1: this.tag1,
      tag2: this.tag2,
      lastModificationDate: this.lastModificationDate,
      rank: this.rank,
      dimensionAge: this.dimensionAge,
      dimensionPercentComplete: this.dimensionPercentComplete,
      dimensionRank: this.dimensionRank,
      dimensionExceptionCode: this.dimensionExceptionCode,
      dimensionReadyStatus: this.dimensionReadyStatus,
      driveLetter: this.driveLetter,
      misc: this.misc,  // var to pass optional info (kludge).
      emi: this.emi,
      multipleTowers: this.multipleTowers,
      highAltitude: this.highAltitude,
      underConstruction: this.underConstruction,
      roadAccessIssues: this.roadAccessIssues,
      otherConditions: this.otherConditions,
      age: this.age,
      collectionShipped: this.collectionShipped,
      collectionTracking: this.collectionTracking,
      collectionReceived: this.collectionReceived,
      groundShipped: this.groundShipped,
      groundTracking: this.groundTracking,
      groundReceived: this.groundReceived,
      liDARShipped: this.liDARShipped,
      liDARTracking: this.liDARTracking,
      liDARReceived: this.liDARReceived
    };
  }

  // Make sure id is defined.
  private normalizeDimensionId(dimName: string): number
  {
    if(this.dimensionName2Id === undefined) return -1;
    const id = this.dimensionName2Id.get(dimName);
    return id === undefined ? -1 : id;
  }

  get twoDimensionDeliverables() {
    return this.deliverables.filter(({ dimensionId }) => dimensionId === this.normalizeDimensionId('Collect'));
  }

  get otherDimensionDeliverables() {
    return this.deliverables.filter(({ dimensionId }) => dimensionId === this.normalizeDimensionId('Process'));
  }

  // Update a job request.
  // Input:
  //   newData: fields with new data.
  update(newData: Partial<JobRequestDTO>): JobRequestDTO {
    const allData = { ...this.asJson, ...newData }; // using 'spread' operators to merge and overwrite current properties in 'this' with those from new data.

    Object.assign(this, allData); // Set properties.

    return this.asJson;
  }
}
